/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { isPhone } from "common/utils/browser";

export const MIN_DIMENSION_SIZE = 0.5;
export const MIN_DIMENSION_PUFF = {
  width: 3,
  height: 2
};

export const DEFAULT_SIZE = 1;
export const MIN_DPI = 200;
export const PRINT_AS_IS_MIN_DPI = 300;
export const PRINT_AS_IS_MAX_HEIGHT = 20; // PRINT_AS_IS_MAX_HEIGHT in feet
export const PRINT_AS_IS_MIN_WIDTH = 22;
export const GANG_SHEET_PIXELS_IN_INCH = 300;
export const GANG_SHEET_REQUIRED_WIDTH_INCHES = 22;
export const GANG_SHEET_REQUIRED_WIDTH_PX =
  GANG_SHEET_PIXELS_IN_INCH * GANG_SHEET_REQUIRED_WIDTH_INCHES; // 6600px
export const GANG_SHEET_MIN_HEIGHT_INCHES = 24;
export const GANG_SHEET_MIN_HEIGHT_PX = GANG_SHEET_PIXELS_IN_INCH * GANG_SHEET_MIN_HEIGHT_INCHES; // 7200px
export const GANG_SHEET_WIDTH_ERROR = "gang_sheet_width_error";
export const GANG_SHEET_HEIGHT_ERROR = "gang_sheet_height_error";
export const PDP_TYPE_GANG_SHEETS = "gang_sheets";
export const MAX_SIZE_FIELD_VALUE = 300;
export const UPSCALE_THRESHOLD = 10; // new width/height should not exceed 10 times the original width/height
export const MAX_QUANTITY_VALUE = 9999;
export const INCHES_PER_FOOT = 12;

// NOTE: The default width and height values here should also reflect in
// Dtf::PdpDefaultValues. Remember to update the values in the service class if any changes
// to the default size / quantity has to be made. This is need to make sure we are passing
// the correct landing page pricing to Google Merchant Center.
//   * dimensions
//   * quantities
export const GANG_DEFAULT_VALUES = Object.freeze({
  width: 22,
  height: 2,
  lengthFoot: 2,
  quantity: 1,
  previewMetadata: { heightInch: 24 }
});

export const DEFAULT_PUFF_VALUES = Object.freeze({
  image: null,
  width: 3,
  height: 3,
  quantity: 10
});

export const DEFAULT_SPANGLE_VALUES = Object.freeze({
  image: null,
  width: 6,
  height: 6,
  quantity: 10
});

export const JERSEY_NUMBERS_DEFAULT_VALUES = Object.freeze({ width: 4, height: 4, quantity: null });

export const DEFAULT_VALUES = Object.freeze({
  width: DEFAULT_SIZE,
  height: DEFAULT_SIZE,
  quantity: 1
});

export const LETTER_DEFAULTS = {
  font: null,
  maxLength: null,
  showCustomHeight: false,
  showCustomMaxLength: false,
  texts: []
};

export const LETTERS_FILL_IN_VALUES = {
  style: "straight",
  font: "promesh",
  max_length_inch: 9,
  letter_height_inch: 2,
  color: "#000000",
  texts: ["ABCD"],
  quantity: 1
};

// MN-9366: shown under the active Letters text box when the
// generate_letters_preview request fails. Without it a failed preview left
// Add To Cart disabled forever with no feedback (the failure was silently
// swallowed), which customers reported as "add to cart button greyed out".
export const LETTERS_PREVIEW_FAILED_MESSAGE =
  "We couldn't generate your preview. Please wait a moment and try typing again.";

// HB-1978: backoff schedule for the DTF artwork upload retry loop. Each retry is a
// fresh POST /api/users/dtf_artworks, which the production WAF rate-limits to 10
// requests / 60s / IP (TEMP-DtfArtworksRateLimit). The previous schedule
// ([1s×5, 5s×5, 10s×5], 15 retries) fired ~11 requests within the first 30s — the
// client's own retry storm tripped the WAF, returning a CloudFront 403 "Request
// blocked" that surfaced as the generic "Image upload failed". This schedule fires
// at t=0,3,9,20,38,68s — at most 5 requests in any rolling 60s window — staying
// under the WAF budget while still recovering quickly from a transient blip.
// NOTE: this bounds a SINGLE upload. The PDP's multi-file flow starts one
// uploadArtwork loop per file (addFilesToQueue, not awaited), so several
// concurrently-failing files still share — and can exceed — the per-IP budget. The
// client cannot fully own a per-IP limit it doesn't observe (concurrent uploads,
// other tabs, NAT'd IPs); MN-9402 relaxes the WAF rule server-side to cover those.
export const RETRY_INTERVALS = [3000, 6000, 11000, 18000, 30000];

// These keys needed to be kept as snakecase on mixpanel
export const MIXPANEL_METADATA_KEYS = [
  "font_details",
  "total_images",
  "total_texts",
  "total_shapes",
  "enhancements"
];

export const GOLD_FOIL_HEX = "#FFD700";

export const MAX_PRINT_SIZES_GROUPED = {
  custom: [],
  "full-front": [
    { category: "adult", sizeRange: "XS - S", width: 10, height: 12 },
    { category: "adult", sizeRange: "M - L", width: 11, height: 14 },
    { category: "adult", sizeRange: "XL", width: 12, height: 14 },
    { category: "adult", sizeRange: "XXL+", width: 13, height: 15 },
    { category: "youth", sizeRange: "XS - S", width: 7, height: 7 },
    { category: "youth", sizeRange: "M - L", width: 8, height: 8 },
    { category: "youth", sizeRange: "XL", width: 9, height: 9 },
    { category: "toddler", sizeRange: "5T - 6T", width: 6, height: 6 },
    { category: "toddler", sizeRange: "2T - 4T", width: 5, height: 5 },
    { category: "infant", sizeRange: "6M - 24M", width: 4, height: 4 },
    { category: "infant", sizeRange: "Newborn", width: 3, height: 3 }
  ],

  "full-back": [
    { category: "adult", sizeRange: "XS - M", width: 10, height: 13 },
    { category: "adult", sizeRange: "L+", width: 12, height: 15 },
    { category: "youth", sizeRange: "XS - S", width: 9, height: 12 },
    { category: "youth", sizeRange: "M+", width: 10, height: 13 },
    { category: "toddler", width: 8, height: 10 },
    { category: "infant", width: 6, height: 8 }
  ],

  "left-chest": [
    { category: "adult", sizeRange: "XS - M", width: 3, height: 3 },
    { category: "adult", sizeRange: "L+", width: 3.5, height: 3.5 },
    { category: "youth", sizeRange: "XS - M", width: 2.75, height: 2.75 },
    { category: "youth", sizeRange: "L+", width: 3, height: 3 },
    { category: "toddler", width: 2, height: 2 },
    { category: "infant", width: 1.5, height: 1.5 }
  ],

  sleeve: [
    { category: "adult", sizeRange: "XS - S", width: 2.5, height: 2.5 },
    { category: "adult", sizeRange: "M - XL", width: 3, height: 3 },
    { category: "adult", sizeRange: "XXL+", width: 3.25, height: 3.25 },
    { category: "youth", width: 2.5, height: 2.5 },
    { category: "toddler", width: 2.25, height: 2.25 },
    { category: "infant", width: 1.5, height: 1.5 }
  ],

  "back-collar": [
    { category: "adult", sizeRange: "XS - L", width: 3, height: 3 },
    { category: "adult", sizeRange: "L+", width: 3.5, height: 3.5 },
    { category: "youth", width: 2.5, height: 2.5 },
    { category: "toddler", width: 2, height: 2 },
    { category: "infant", width: 1.75, height: 1.75 }
  ]
};

export const PRESET_MOCKUP_COLORS = [
  { name: "Black", value: "#000000", isModalColor: false },
  { name: "White", value: "#FFFFFF", isModalColor: false },
  { name: "Gray", value: "#BDBBBC", isModalColor: false },
  { name: "Pink", value: "#F7C9D4", isModalColor: false },
  { name: "Red", value: "#D3113B", isModalColor: false },
  { name: "Navy", value: "#031A41", isModalColor: false },
  { name: "Light brown", value: "#EDE4D3", isModalColor: true },
  { name: "Dark brown", value: "#D8C6AF", isModalColor: true },
  { name: "Blue", value: "#106CB1", isModalColor: false }
];

export const MIN_STICKER_SIZE = 3;

export const UPLOAD_BUTTON_LABEL = "My Device";

// Placeholder substituted at render time with the server-derived bulk-sticker
// price (store.stickerPromoPrice). The tip is dropped when no price is available.
export const STICKER_PROMO_PRICE_TOKEN = "{promoPrice}";

export const ARTWORK_IMAGE_MESSAGES = {
  transfers: [
    "Make those neons & gradients pop - try ProColor! 🎨",
    "Thin lines? Keep ’em at least 0.25 inches thick! ✏️",
    "Transparent backgrounds = Instant magic! 🌟",
    "Small fonts? Use 7.5px minimum! 🔍",
    "Jiffy Transfers: Blink and they’re done! ⏱️",
    "Heat press, peel when cool ❄️",
    "Machine wash inside out for a long-lasting love affair 👕",
    "Glossy finish? Teflon’s got you covered! ✨",
    "Matte finish? Parchment paper to the rescue! 💪",
    "Cotton and fabric blends love Jiffy Transfers 😍",
    "Add sparkle with glitter transfers! ✨",
    "Royal touch? Go for gold foil! 👑",
    "Get some night-time bling with glow-in-the-dark transfers! 🌙",
    "Reflective prints for safety and style! 🚸",
    "Thermochromic designs react to heat! 🔥",
    "Pearl prints add a blue-green sheen! 🦚",
    "Pre-press to keep it wrinkle-free 🧺",
    "Jiffy transfers: print and press, no prep! ⏳",
    "Use SVG for scalable vector designs 📐",
    "Optimized for PNG files 🎉",
    "Tumble dry low and keep the prints pro! 🔄",
    "Bold designs with reflective or foil transfers! 😎",
    "High polyester blends? Try sublimation! 🧪",
    "Intricate designs made easy with Jiffy Transfers 🥳",
    "No setup costs, just print and press! 🤑",
    "No minimum orders, because why limit creativity? 💸",
    "Save time, save money, press in seconds 💰",
    "Photo-quality prints with ProColor magic 🪄",
    "Save time, grow your business with Jiffy Transfers 📈",
    "Ultra-durable prints, shipped same-day! 📦",
    "Softer to touch than HTV 💅",
    "Print on more fabrics than ever before 🤩",
    "No equipment to maintain with Jiffy Transfers, just bring the heat! 🔥"
  ],

  stickers: [
    "Dark backgrounds are perfect for your white designs 🕶️",
    "No email proofing needed, your stickers will print exactly as previewed ✉️",
    "High-quality and durable stickers, fast production and delivery 💨",
    "The more you buy, the cheaper each sticker will be 🤑",
    `You can get 100 2" stickers for only ${STICKER_PROMO_PRICE_TOKEN}! ✓`,
    "Die-cut stickers follow the exact contour of your image 📏",
    "Our high quality vinyl makes it easy to peel and apply 🪶",
    "Jiffy stickers are the perfect gift to delight your customers 🙂",
    "Convert your transfers into die-cut stickers to complete your project 🔁",
    "Customize your border size and background color in 1 click 🖱️"
  ]
};

// Resolves the rotating sticker tips for display: substitutes the server-derived
// bulk price into the promo tip, or drops that tip when no price is available so
// the overlay never renders the raw placeholder.
export function resolveStickerTips(tips: string[], promoPrice: string | null): string[] {
  return tips.reduce<string[]>((resolved, tip) => {
    if (!tip.includes(STICKER_PROMO_PRICE_TOKEN)) {
      resolved.push(tip);
    } else if (promoPrice) {
      resolved.push(tip.replace(STICKER_PROMO_PRICE_TOKEN, promoPrice));
    }
    return resolved;
  }, []);
}

// SingleImageZoom constants
export const SINGLE_IMAGE_ZOOM = Object.freeze({
  NATURAL_HEIGHT_THRESHOLD: 1000,
  DEFAULT_IMAGE_HEIGHT: "80vh",
  STICKER_MAX_HEIGHT: "60vh",
  INLINE_MAX_HEIGHT: "50vh",
  FULL_WIDTH_IMAGE_WIDTH: "90vw"
});

export const BACKGROUND_TYPES = { CHECKERED: "checkered", GREEN: "green" };

// DTF catalog number constants
export const DTF001_CATALOG_NUMBER = "DTF001";

// MN-8435: Polling-fallback allowlist for DTF PDP ActionCable disconnects.
//
// DTF001 is always enabled (legacy MN-4782 path). The remaining 14 catalogs are
// gated behind Flip flag :dtf_polling_fallback_all_catalogs (default off) via
// store.dtfPollingFallbackAllCatalogs and the gate in schedulePollingFallback.
//
// EXCLUDED catalogs and the reasons (see tmp/MN-8435/brainstorm.md):
//   - GANGBLDR001 — Gang Sheet Builder canvas; different lifecycle (only 32 of
//     3,337 7-day artworks reach :processed). Polling would render spurious
//     timeouts for the normal builder flow.
//   - NL001 — Neck Labels; out of ticket scope — separate decision (highest
//     prod stuck rate at 31%, but lifecycle distinction vs DTF001 not yet
//     confirmed). Tracked in MN-8657.
//   - STKR-DC001, STKR-SH001, STKR-SMPL001 — stickers; different enhancement
//     pipeline.
//   - SPNG-HGR001, SPNG-GLS001, SPNG-RNB001 — spangles; separate spangle_update
//     cable action at dtf-channel.ts:632-639.
//   - PUFF001 — puff transfers; surface-area pricing + different pipeline.
//   - DTFLN1 (Jersey Numbers), DTFLN2 (Letters) — non-image upload products per
//     Constants::NON_IMAGE_UPLOAD_PRODUCTS (lib/constants.rb:1250-1254); no
//     enhancement pipeline.
//   - POD001 — production on demand; separate enhancement path.
export const POLLING_ENABLED_CATALOG = Object.freeze([
  "DTF001",
  "DTFGANG001",
  "UVDTF01",
  "UVDTFGANG",
  "DTFG01",
  "DTFM01",
  "DTFRF1",
  "DTFGD1",
  "DTFIR1",
  "DTFTM1",
  "DTFPL1",
  "DTFPC1",
  "SUBT001",
  "GANGDTFPC1",
  "GANGDTFG01"
]);
// Shorter polling delay used when WS disconnects during active processing
export const WS_DISCONNECT_POLLING_DELAY = 5000;
// MN-8435: Cosmetic parallelism with POLLING_ENABLED_CATALOG. This constant is
// referenced only by isBackendTriggeredEnhancementEligible() in store.ts, which
// has zero callers — widening here is for visual parity with the load-bearing
// backend twin Constants::BACKEND_TRIGGERED_ENHANCEMENT_CATALOG_NUMBERS at
// lib/constants.rb:1259 (currently still DTF001-only; widening the backend
// constant is out of scope here as it shifts the upload-enqueue path).
// Filed as deferred followup: delete this constant + the dead getter.
export const BACKEND_TRIGGERED_ENHANCEMENT_ELIGIBLE_CATALOG = Object.freeze([
  "DTF001",
  "DTFGANG001",
  "UVDTF01",
  "UVDTFGANG",
  "DTFG01",
  "DTFM01",
  "DTFRF1",
  "DTFGD1",
  "DTFIR1",
  "DTFTM1",
  "DTFPL1",
  "DTFPC1",
  "SUBT001",
  "GANGDTFPC1",
  "GANGDTFG01"
]);

export const DTF_TRANSFER_USER_RETENTION_IMAGES = Object.freeze({
  LEGACY_BEFORE_IMAGE_URL: "https://jiffy.imgix.net/2/gen/before.png",
  LEGACY_AFTER_IMAGE_URL: "https://jiffy.imgix.net/2/gen/after.png",
  PIN_BEFORE_IMAGE_URL: "https://jiffy.imgix.net/2/gen/user-retention-modal-pin-before.png",
  PIN_AFTER_IMAGE_URL: "https://jiffy.imgix.net/2/gen/user-retention-modal-pin-after.png"
});

export const DTF_TRANSFER_USER_RETENTION_CHANGES = Object.freeze([
  isPhone() ? "Ribbon changed" : "Ribbon color changed",
  "Freckles removed",
  "Background removed",
  "Text changed"
]);

export const IS_DTF_USER_RETENTION_MODAL_SHOWN = "IS_DTF_USER_RETENTION_MODAL_SHOWN";

export const MODAL_TRIGGER_POINTS = {
  BACK_BUTTON: "back-button",
  NONE: "none"
};
